import { getCurrentInstance, onBeforeUnmount, onMounted, readonly, ref, watch } from 'vue'

/**
 * 📱 M6 (mobile gameplan): Android's back button, one rule for every screen.
 *
 * Every cover on screen is a LAYER, pushed when it opens and dropped when it
 * closes; back closes the newest one, exactly as its own ✕ (or Keep, or
 * Cancel) would. Every card with a ModalClose registers itself; the few
 * covers without one register beside their open flag. A cover that only
 * moves on through its own buttons - a result card, the act summary, a node
 * transition, the run-conflict chooser - is a HOLD: back does nothing while
 * it is up.
 *
 * With no cover up, the SCREEN answers (the newest handler a page put in):
 * the node screen raises the pause veil, and a second back opens the menu;
 * the map opens the menu; anything else returns to the title; the title arms
 * a second press, and that one leaves the app. Back never walks the browser
 * history, so it can never take a player out of a fight.
 *
 * The native shell calls handleBack() from @capacitor/app's backButton
 * (plugins/native-shell.client.ts); the browser suites call window.__edBack(),
 * which answers with what happened (a BackOutcome).
 */
export type BackOutcome = 'closed' | 'held' | 'screen' | 'exit-armed' | 'exit'

/** Closes the cover; answering `false` means it could not close just now (held). */
export type BackClose = () => void | boolean

interface Layer { id: number; name: string; close: BackClose | null; priority: number }

const layers: Layer[] = []
const screens: Array<{ id: number; handle: () => BackOutcome }> = []
const menus: Array<{ id: number; open: () => void }> = []
let nextId = 1

/** Two presses this close together on the title leave the app. */
export const BACK_EXIT_WINDOW_MS = 2000
const exitArmed = ref(false)
let exitTimer: ReturnType<typeof setTimeout> | undefined
let exitApp: (() => void) | null = null
let fallback: (() => BackOutcome) | null = null

function drop<T extends { id: number }>(list: T[], id: number) {
  const i = list.findIndex(entry => entry.id === id)
  if (i >= 0) list.splice(i, 1)
}

/** Push a layer now; the returned function drops it. `close: null` = a hold.
 * Higher priority keeps a global modal above covers mounted during navigation. */
export function pushBackLayer(name: string, close: BackClose | null, priority = 0): () => void {
  const layer = { id: nextId++, name, close, priority }
  layers.push(layer)
  return () => drop(layers, layer.id)
}

/**
 * A cover for back to close while `open()` is true (in a component's setup).
 * `close: null` makes it a hold: back is swallowed until the cover goes.
 */
export function useBackLayer(name: string, open: () => boolean, close: BackClose | null, priority = 0) {
  let release: (() => void) | null = null
  const stop = watch(open, (isOpen) => {
    if (isOpen && !release) release = pushBackLayer(name, close, priority)
    else if (!isOpen && release) { release(); release = null }
  }, { immediate: true, flush: 'sync' })
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stop()
      release?.()
      release = null
    })
  }
}

/** Adds `entry` while the calling component is mounted (at once outside one);
 * the returned function takes it out early. */
function whileMounted<T extends { id: number }>(list: T[], entry: T): () => void {
  const release = () => drop(list, entry.id)
  if (!getCurrentInstance()) {
    list.push(entry)
    return release
  }
  onMounted(() => list.push(entry))
  onBeforeUnmount(release)
  return release
}

/** The page's own answer when no cover is up; the newest mounted page wins. */
export function useBackScreen(handle: () => BackOutcome): () => void {
  return whileMounted(screens, { id: nextId++, handle })
}

/** A mounted game menu that back may open (GameMenu). */
export function useBackMenu(open: () => void): () => void {
  return whileMounted(menus, { id: nextId++, open })
}

/** Opens the newest mounted game menu; false when there is none. */
export function openBackMenu(): boolean {
  const menu = menus[menus.length - 1]
  if (!menu) return false
  menu.open()
  return true
}

/** The title's answer: arm, then leave on a second press inside the window. */
export function backExitOrArm(): BackOutcome {
  if (exitArmed.value) {
    exitArmed.value = false
    clearTimeout(exitTimer)
    exitApp?.()
    return 'exit'
  }
  exitArmed.value = true
  clearTimeout(exitTimer)
  exitTimer = setTimeout(() => { exitArmed.value = false }, BACK_EXIT_WINDOW_MS)
  return 'exit-armed'
}

/** Wired once by the native-shell plugin: what "leave the app" does, and
 * what back does on a screen that put in no handler of its own. */
export function configureBack(options: { exit?: () => void; fallback?: () => BackOutcome }) {
  if (options.exit) exitApp = options.exit
  if (options.fallback) fallback = options.fallback
}

export function handleBack(): BackOutcome {
  let top: Layer | undefined
  for (const layer of layers) {
    if (!top || layer.priority >= top.priority) top = layer
  }
  if (top) {
    if (!top.close) return 'held'
    return top.close() === false ? 'held' : 'closed'
  }
  const screen = screens[screens.length - 1]
  if (screen) return screen.handle()
  return fallback ? fallback() : 'held'
}

/** For the suites: the open layers, oldest first. */
export function backLayerNames(): string[] {
  return layers.map(layer => (layer.close ? layer.name : `${layer.name} (hold)`))
}

export function useBackStack() {
  return { exitArmed: readonly(exitArmed), handleBack, openBackMenu, backExitOrArm }
}
